import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import api from '../api/axios';
import TimeZoneList from './TimeZoneList';
import { useProfile } from '../contexts/ProfileContext';
import { generateCompliantPassword, validatePassword, passwordPolicyHint } from '../utils/passwordPolicy';

export default function ProfileUpdate() {
  const [formData, setFormData] = useState({
    userId: '',
    userPwd: '',
    userCPwd: '',
    firstname: '',
    lastname: '',
    userEmail: '',
    timeZone: ''
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showUserPwd, setShowUserPwd] = useState(false);
  const [showUserCPwd, setShowUserCPwd] = useState(false);
  const { profile, loading } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
  //   const fetchProfile = async () => {
  //     try {
  //       const response = await api.get('/webapi/myprofile');
  //       const { userId, firstname, lastname, userEmail, timeZone } = response.data;
  //       setFormData(prev => ({
  //         ...prev,
  //         userId: userId || '',
  //         firstname: firstname || '',
  //         lastname: lastname || '',
  //         userEmail: userEmail || '',
  //         timeZone: timeZone || '+00.00 UTC',
  //       }));
  //     } catch (err) {
  //       console.error("Error fetching profile:", err);
  //       setError("Failed to load profile data.");
  //     }
  //   };
  //   fetchProfile();
  // }, []);
  if (profile) {
      setFormData(prev => ({
        ...prev,
        userId: profile.userId || '',
        firstname: profile.firstname || '',
        lastname: profile.lastname || '',
        userEmail: profile.userEmail || '',
        timeZone: profile.timeZone || '+00.00 UTC',
      }));
    }
  }, [profile]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGeneratePassword = () => {
    const generatedPassword = generateCompliantPassword();
    setFormData(prev => ({
      ...prev,
      userPwd: generatedPassword,
      userCPwd: generatedPassword
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    if (formData.userPwd || formData.userCPwd) {
      if (formData.userPwd !== formData.userCPwd) {
        setError('Passwords do not match.');
        return;
      }
      const policyError = validatePassword(formData.userPwd);
      if (policyError) {
        setError(policyError);
        return;
      }
    }

    try {
      await api.put('/webapi/myprofile', formData);
      setSuccessMessage('Profile updated successfully!');
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(err.response?.data?.error || 'Failed to update profile.');
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl ">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Update</h1>
          <p className="text-gray-600">My Profile</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
        {/* Main Form Container */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Login Access Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Login Access</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Username</label>
              <input
                type="text"
                name="username"
                value={formData.userId}
                onChange={handleInputChange}
                autoComplete="off"
                className="w-full max-w-xl px-4 py-3 bg-gray-100 border border-gray-200 rounded text-gray-800"
                readOnly
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="block text-gray-700">Password</label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showUserPwd ? 'text' : 'password'}
                    name="userPwd"
                    value={formData.userPwd}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUserPwd(!showUserPwd)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showUserPwd ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Confirm password</label>
                <div className="relative">
                  <input
                    type={showUserCPwd ? 'text' : 'password'}
                    name="userCPwd"
                    value={formData.userCPwd}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUserCPwd(!showUserCPwd)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showUserCPwd ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-8">
              {passwordPolicyHint}
            </p>

            <div className="h-1 bg-[radial-gradient(circle_at_center,theme(colors.red.300)_1px,transparent_1px)] [background-size:6px_6px] bg-repeat-x"></div>
          </div>

          {/* Info and Preference Section */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Info and Preference</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-600 text-sm mb-2">First name</label>
                <input
                  type="text"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-600 text-sm mb-2">Last name / surname</label>
                <input
                  type="text"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-600 text-sm mb-2">New email address</label>
              <input
                type="email"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleInputChange}
                className="w-full max-w-xl px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-8">
              <label className="block text-gray-600 text-sm mb-2">Time Zone</label>
              <div className="relative w-full max-w-xl">
                <TimeZoneList
                  selectedTimeZone={formData.timeZone}
                  onChange={(value) => setFormData({...formData, timeZone: value})} // updates state automatically
                />
{/* 
                <select
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="+00.00 UTC">+00.00 UTC</option>
                  <option value="+01.00 UTC">+01.00 UTC</option>
                  <option value="+08.00 UTC">+08.00 UTC (Singapore)</option>
                  <option value="-05.00 UTC">-05.00 UTC (EST)</option>
                  <option value="-08.00 UTC">-08.00 UTC (PST)</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none" size={20} />
               */}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                <Check size={20} />
                Save
              </button>
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
